/* ---------------------------------------------------------------------------
   What it costs when a structure is out of service.

   The other half of `GR-08`. That module screens EXPOSURE — what might put a
   structure out of service. This one describes CONSEQUENCE — what happens to
   the network when it is. They are two axes and they are deliberately reported
   side by side rather than multiplied together.

   **There is no criticality index here, and that is the whole design.** It
   would have been three lines to weight AADT against detour time and emit a
   number out of 100, and the number would have been worse than useless: a rural
   crossing carrying 200 vehicles past a 90 km diversion and a highway bridge
   carrying 9,000 past a 3 km one would land in the same band for opposite
   reasons, and nothing on screen would say which. The incumbent DoR system
   prioritises on road classification, traffic volume and detour time; matching
   that vocabulary is what makes this data useful to somebody who already works
   with it. Inventing a different index would not.

   **Road classification is derived, never stored.** The route code already
   carries it, so a stored copy could only ever drift from the code beside it on
   the same record. See `routeClass`.

   **Everything else is null until somebody records it**, and null is drawn
   differently from zero everywhere it appears. On today's five records all four
   fields are null on all five, because a General Arrangement drawing does not
   carry traffic counts — which is honest, and is why `GR-12` was about making
   the fields exist, editable and importable rather than about filling them in.
   --------------------------------------------------------------------------- */

import type { Bridge } from './types.ts';
import { active } from '../tenant/registry.ts';
import { classifyRoad } from '../tenant/roads.ts';

/* --------------------------- road classification --------------------------- */

/**
 * What kind of road this is, read off the route code.
 *
 * DoR's Strategic Road Network numbers national highways `H##` and feeder roads
 * `F###`; district and provincial roads carry `D`/`PR`-style codes. The letter
 * IS the classification, which is why this is a function and not a column.
 *
 * An unrecognised prefix returns `null` rather than a guess or a default of
 * "district road" — the register imports from spreadsheets, and a code nobody
 * has seen before is a thing to notice rather than to quietly bucket.
 */
/**
 * What a road code classifies as — whichever authority's codes they are.
 *
 * A STRING, NOT A UNION, SINCE TENANCY. This used to be the four values Nepal
 * has, spelled out in the type, with the four prefix rules in the body of this
 * function. Both now live in the tenant profile (`roadClasses` in
 * src/tenant/types.ts), because the Philippines does not classify roads the way
 * Nepal does — and today does not classify them at all, since the DPWH register
 * carries highway names and no route codes. A union here would have been a
 * compile-time claim that every department uses Nepal's four classes.
 *
 * Still null for an unrecognised code, and null for a record with no code, and
 * the two are deliberately the same answer: neither is classifiable, and the
 * register says *not recorded* for both rather than inventing a class.
 */
export type RoadClass = string;

export function routeClass(route: string | null): RoadClass | null {
  return classifyRoad(route, active().roadClasses);
}

/* --------------------------- traffic --------------------------- */

/**
 * How old a traffic count may be before it is shown as dated, in years.
 *
 * **Not from a standard.** Nepal's national traffic counts are periodic rather
 * than annual, and this is a display threshold, not a validity rule: past it the
 * figure is still shown, still quoted, and marked with its age. Five years is
 * about the interval over which traffic on a Nepali feeder road can plausibly
 * change enough to change a decision.
 */
export const AADT_STALE_YEARS = 5;

/**
 * How a traffic count should be read: its age, and whether that matters.
 *
 * A count with no year is the interesting case. It is NOT treated as current —
 * a figure somebody typed with no idea when it was measured is the one most
 * likely to be quoted as though it were this year's.
 */
export interface TrafficReading {
  aadt: number | null;
  year: number | null;
  /** Years since the count, or null where it cannot be worked out. */
  age: number | null;
  /** Whether the figure should be shown as dated. */
  stale: boolean;
  /** One line fit for a fact row or a tooltip. */
  note: string;
}

export function trafficReading(b: Bridge, now: Date = new Date()): TrafficReading {
  if (b.aadt == null) {
    return { aadt: null, year: b.aadtYear, age: null, stale: false, note: 'No traffic count on file.' };
  }

  if (b.aadtYear == null) {
    return {
      aadt: b.aadt,
      year: null,
      age: null,
      stale: true,
      note: 'Counted, but the record does not say when — so this cannot be read as current traffic.',
    };
  }

  const age = now.getFullYear() - b.aadtYear;
  return {
    aadt: b.aadt,
    year: b.aadtYear,
    age,
    stale: age > AADT_STALE_YEARS,
    note:
      age > AADT_STALE_YEARS
        ? `Counted in ${b.aadtYear}, ${age} years ago. Old enough to be worth re-counting before it decides anything.`
        : `Counted in ${b.aadtYear}.`,
  };
}

/* --------------------------- the whole picture --------------------------- */

/**
 * One line of consequence data, ready to draw.
 *
 * The same shape `ScreeningInput` uses, and for the same reason: what is absent
 * has to be as visible as what is present, or a screen full of blanks reads as a
 * screen full of zeroes.
 */
export interface ConsequenceFact {
  label: string;
  value: string;
  known: boolean;
  note?: string | undefined;
}

/** Whether anything at all has been recorded about what a closure would cost. */
export function hasConsequence(b: Bridge): boolean {
  return b.aadt != null || b.detourKm != null || b.detourMinutes != null || b.lifeline != null;
}

/**
 * Everything the register holds about what a closure here would cost.
 *
 * Road class first because it is the one line that is always answerable, and it
 * is the input the incumbent system weighs most heavily.
 */
export function consequenceFacts(b: Bridge, now: Date = new Date()): ConsequenceFact[] {
  const cls = routeClass(b.route);
  const traffic = trafficReading(b, now);

  return [
    {
      label: 'Road classification',
      value:
        cls ??
        (b.route == null
          ? 'No road code recorded'
          : `${b.route} \u2014 unrecognised code`),
      known: cls !== null,
      note: cls === null ? 'Read from the route code, which this build does not recognise.' : undefined,
    },
    {
      label: 'Traffic (AADT)',
      value: traffic.aadt == null ? '—' : `${traffic.aadt.toLocaleString('en-GB')} veh/day`,
      known: traffic.aadt != null && !traffic.stale,
      note: traffic.note,
    },
    {
      label: 'Detour if closed',
      value: b.detourKm == null ? '—' : `${b.detourKm.toFixed(1)} km`,
      known: b.detourKm != null,
      note:
        b.detourKm === 0
          ? 'Zero: another crossing carries the same traffic. Not the same as unrecorded.'
          : undefined,
    },
    {
      label: 'Detour time',
      value: b.detourMinutes == null ? '—' : `${b.detourMinutes} min`,
      known: b.detourMinutes != null,
      note:
        b.detourKm != null && b.detourMinutes == null
          ? 'A distance without a time says little on a mountain road.'
          : undefined,
    },
    {
      label: 'Lifeline route',
      value: b.lifeline == null ? '—' : b.lifeline ? 'Yes' : 'No',
      known: b.lifeline != null,
      note:
        b.lifeline == null
          ? 'Nobody has recorded whether emergency access depends on this crossing.'
          : undefined,
    },
  ];
}

/* --------------------------- across the register --------------------------- */

export interface ConsequenceCoverage {
  /** Structures with at least one consequence field recorded. */
  recorded: number;
  total: number;
  /** Structures carrying a traffic count, however old. */
  counted: number;
  /** Of those, how many are older than AADT_STALE_YEARS or carry no year at all. */
  dated: number;
  /** Structures flagged as lifeline routes. */
  lifelines: number;
}

/**
 * How much of the register can be prioritised on consequence at all.
 *
 * A count rather than an average, because an average over four recorded values
 * out of five hundred is a number that looks like knowledge. What a planner
 * needs to know first is how much of the network is missing from the picture.
 */
export function consequenceCoverage(
  list: readonly Bridge[],
  now: Date = new Date(),
): ConsequenceCoverage {
  let recorded = 0;
  let counted = 0;
  let dated = 0;
  let lifelines = 0;

  for (const b of list) {
    if (hasConsequence(b)) recorded++;
    if (b.aadt != null) {
      counted++;
      if (trafficReading(b, now).stale) dated++;
    }
    if (b.lifeline === true) lifelines++;
  }

  return { recorded, total: list.length, counted, dated, lifelines };
}
